/**
 * The app-wide "current plant".
 *
 * pnid has never had one: the plant page keeps its choice in the URL plus
 * `pnid_last_plant_domain_id`, the drawings library filters on the display-name
 * string, App.tsx lifts its own DomainScope, and policy admin keeps a fourth.
 * The shell's identity tile needs a single answer, so this module owns the
 * small shared slice — the last plant the user actually opened — and nothing
 * more. It deliberately does NOT fetch: reading a name the app already
 * displayed costs no request, and mounting a plants query on every shell route
 * would change when `GET /plants` fires for every page in the app.
 */

import type { PlantSummary } from '../types';

const ID_KEY = 'pnid_last_plant_domain_id';
const NAME_KEY = 'pnid_last_plant_name';
/* Drawings only ever carry a LEAF plant name — "Train-1", never "Ammonia" —
   so matching a parent plant by its own name alone returns nothing at all.
   The switcher knows the hierarchy at the moment of the choice, so it records
   the whole family here and no page needs a plants query to expand it. */
const NAMES_KEY = 'pnid_last_plant_names';

/** Fires when the plant changes in this tab; `storage` only covers other tabs. */
export const PLANT_SCOPE_EVENT = 'pnid:plant-scope';

export type PlantScope = {
  domainId: string;
  name: string;
  /** The scoped plant plus its child plants — what a drawing may match. */
  names: string[];
};

export function readPlantScope(): PlantScope {
  try {
    const name = localStorage.getItem(NAME_KEY) || '';
    let names: string[] = [];
    try {
      const raw = localStorage.getItem(NAMES_KEY);
      const parsed = raw ? JSON.parse(raw) : null;
      if (Array.isArray(parsed)) names = parsed.filter((v) => typeof v === 'string');
    } catch {
      /* A hand-edited or half-written value just means no family. */
    }
    return {
      domainId: localStorage.getItem(ID_KEY) || '',
      name,
      names: names.length ? names : (name ? [name] : []),
    };
  } catch {
    /* Site data blocked: the shell falls back to the unscoped label. */
    return { domainId: '', name: '', names: [] };
  }
}

/* Record the family, not just the plant. Drawings carry leaf plant names
   only, so scoping to a parent like "Ammonia" by its own name matches
   nothing — the switcher is the one place that holds the hierarchy. */
export function plantFamilyNames(plant: PlantSummary, plants: PlantSummary[]): string[] {
  return [
    plant.plant,
    ...plants
      .filter((item) => (item.parent || '').trim() === plant.plant)
      .map((item) => item.plant),
  ];
}

export function rememberPlantScope(domainId: string, name?: string, names?: string[]) {
  /* Idempotent on purpose. Callers write this from render effects, and an
     unconditional dispatch turns "write the scope" into a render loop: the
     write fires PLANT_SCOPE_EVENT, a subscriber moves the URL, that re-renders,
     the effect writes again — React reported "Maximum update depth exceeded" on
     the plant page. Only a real change is published. */
  const current = readPlantScope();
  const sameNames = names?.length
    ? names.length === current.names.length && names.every((v, i) => v === current.names[i])
    : true;
  if (
    (!domainId || domainId === current.domainId)
    && (!name || name === current.name)
    && sameNames
  ) {
    return;
  }

  try {
    if (domainId) localStorage.setItem(ID_KEY, domainId);
    if (name) localStorage.setItem(NAME_KEY, name);
    if (names?.length) localStorage.setItem(NAMES_KEY, JSON.stringify(names));
    else localStorage.removeItem(NAMES_KEY);
  } catch {
    /* Nothing to recover; the tile shows the unscoped label instead. */
  }
  window.dispatchEvent(new CustomEvent(PLANT_SCOPE_EVENT));
}

export function clearPlantScope() {
  try {
    localStorage.removeItem(ID_KEY);
    localStorage.removeItem(NAME_KEY);
    localStorage.removeItem(NAMES_KEY);
  } catch {
    /* Nothing to clear. */
  }
  window.dispatchEvent(new CustomEvent(PLANT_SCOPE_EVENT));
}

/**
 * Subscribes to the current plant. `PLANT_SCOPE_EVENT` covers this tab —
 * `storage` never fires in the tab that wrote the value — and `storage` covers
 * the others, so a switch in one tab reaches the rest.
 */
export function subscribePlantScope(onChange: () => void) {
  window.addEventListener(PLANT_SCOPE_EVENT, onChange);
  window.addEventListener('storage', onChange);
  return () => {
    window.removeEventListener(PLANT_SCOPE_EVENT, onChange);
    window.removeEventListener('storage', onChange);
  };
}
